import { memo } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/_atoms/button";
import { Input } from "@/components/_atoms/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_atoms/select";
import { InputDate } from "@/components/_molecules/input-date/input-date";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";

import type { CurrentFieldValues } from "../../models/data-request.model";

import type { ValueRow } from "./indicator-value-rows";

type IndicatorDataLine = NonNullable<CurrentFieldValues["dataLineList"]>[number];

interface IndicatorValueRowProps {
  row: ValueRow;
  dataLineList: IndicatorDataLine[];
  disabled?: boolean;
  onUpdate: (clientKey: string, patch: Partial<ValueRow>) => void;
  onRemove: (clientKey: string) => void;
}

function IndicatorValueRowComponent({ row, dataLineList, disabled, onUpdate, onRemove }: IndicatorValueRowProps) {
  const { t } = useDomainTranslation("data-request");

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-40">
        <InputDate
          defaultValue={row.date || undefined}
          onValueChange={(date) => onUpdate(row.clientKey, { date: date ?? "" })}
          disabled={disabled}
          className="h-8 text-sm"
        />
      </div>

      <Input
        type="number"
        value={row.value}
        onChange={(event) => onUpdate(row.clientKey, { value: event.target.value })}
        disabled={disabled}
        className="h-8 w-28 text-sm"
        placeholder={t("dataEntry.values.value")}
        aria-label={t("dataEntry.values.value")}
      />

      <Select
        value={row.dataLineId}
        onValueChange={(dataLineId) => onUpdate(row.clientKey, { dataLineId })}
        disabled={disabled}
      >
        <SelectTrigger className="h-8 w-44 text-sm" aria-label={t("dataEntry.values.dataLine")}>
          <SelectValue placeholder={t("dataEntry.values.dataLine")} />
        </SelectTrigger>
        <SelectContent>
          {dataLineList.map((line) => (
            <SelectItem key={line.id} value={line.id}>
              <span className="flex items-center gap-2">
                {line.color && (
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: line.color }} />
                )}
                {line.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        value={row.comment}
        onChange={(event) => onUpdate(row.clientKey, { comment: event.target.value })}
        disabled={disabled}
        className="h-8 min-w-32 flex-1 text-sm"
        placeholder={t("dataEntry.values.comment")}
        aria-label={t("dataEntry.values.comment")}
      />

      {!row.id && !disabled && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => onRemove(row.clientKey)}
          aria-label={t("dataEntry.values.removeRow")}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export const IndicatorValueRow = memo(IndicatorValueRowComponent);
